import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { QueueCycleTime } from '@App/models';
import { SharedModule } from '@Shared/shared.module';
import { ChartModule, UIChart } from 'primeng/chart';

const CHART_COLORS = [
    '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', 
    '#FF9F40', '#7BC225', '#B325C2', '#C25B25'
];

@Component({
    selector: 'case-cycle-time-per-queue-grouped-bar-chart',
    templateUrl: './case-cycle-time-per-queue-grouped-bar-chart.component.html',
    styleUrl: './case-cycle-time-per-queue-grouped-bar-chart.component.scss',
    imports: [SharedModule, ChartModule]
})
export class CaseCycleTimePerQueueGroupedBarChartComponent implements OnChanges {
    @ViewChild('chart') chart: UIChart;

    @Input() isExpanded = false;
    @Input() queueCycleTimes: QueueCycleTime[] = [];

    chartData: any = {
        labels: [],
        datasets: []
    };
    tooltipLabels: string[] = [];
    // Y-axis unit chosen per render: hours when all bars are under a day, otherwise days
    yAxisUnit: 'hours' | 'days' = 'days';

    chartOptions = {
        animation: {
            duration: 0
        },
        plugins: {
            tooltip: {
                mode: 'nearest',
                intersect: true,
                usePointStyle: true,
                borderWidth: 0,
                callbacks: {
                    title: (context: any) => {
                        const index = context[0]?.dataIndex;
                        return this.tooltipLabels[index];
                    },
                    label: (context: any) => {
                        const label = context.dataset.label;
                        const days = this.yAxisUnit === 'hours' ? context.raw / 24 : context.raw;
                        return [
                            label,
                            this.formatHoursToDayHour(days)
                        ];
                    }
                }
            },
            legend: {
                display: false
            },
        },
        elements: {
            point: {
                pointStyle: 'circle',
                radius: (context: any) => {
                    const value = context.raw;
                    return value === 0 ? 0 : 4;
                }
            }
        },
        scales: {
            y: {
                position: 'right',
                ticks: {
                    font: {
                        size: 12
                    },
                    precision: 0,
                    callback: (value: number) => {
                        if (value === 0) return '0';
                        if (this.yAxisUnit === 'hours') return value + (value === 1 ? ' hour' : ' hours');
                        return value + (value === 1 ? ' day' : ' days');
                    }
                },
                border: {
                    display: false
                }
            },
            x: {
                ticks: {
                    font: {
                        size: 12
                    }
                }
            }
        }
    };

    constructor() { }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['queueCycleTimes'] && this.queueCycleTimes) {
            this.updateChartData();
        }
    }

    updateChartData(): void {
        const monthBuckets = this.getLastSixMonthsBuckets();

        const queueMap = new Map<string, { name: string; data: (number | null)[] }>();

        for (const entry of this.queueCycleTimes) {
            if (!queueMap.has(entry.queueId)) {
                queueMap.set(entry.queueId, {
                    name: entry.queueName,
                    data: new Array(6).fill(null)
                });
            }

            const bucketIndex = monthBuckets.findIndex(
                b => b.year === entry.exitYear && b.month === entry.exitMonth
            );

            if (bucketIndex !== -1) {
                queueMap.get(entry.queueId)!.data[bucketIndex] = entry.avgQueueCycleTimeInHours / 24;
            }
        }

        // queue data is in days; show hours when every bar is under a day
        const entries = Array.from(queueMap.entries());
        const maxDays = Math.max(0, ...entries.flatMap(([, q]) => q.data.filter((v): v is number => v != null)));
        this.yAxisUnit = maxDays < 1 ? 'hours' : 'days';
        const factor = this.yAxisUnit === 'hours' ? 24 : 1;

        const datasets = entries.map(([queueId, queue], index) => ({
            type: 'bar',
            queueId,
            label: queue.name,
            data: queue.data.map(v => v == null ? null : v * factor),
            minBarLength: 10,
            backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
            borderColor: CHART_COLORS[index % CHART_COLORS.length],
        }));

        const buckets = this.getLastSixMonthsBuckets();
        this.tooltipLabels = buckets.map(b => b.fullLabel);

        this.chartData = {
            labels: buckets.map(b => b.label),
            datasets
        };
    }

    
    getLastSixMonthsBuckets(): { year: number; month: number; label: string; fullLabel: string }[] {
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const now = new Date();
        return Array.from({ length: 6 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
            return {
                year: d.getFullYear(),
                month: d.getMonth() + 1,
                label: months[d.getMonth()],
                fullLabel: `${months[d.getMonth()]} ${d.getFullYear()}`
            };
        });
    }

    formatHoursToDayHour(value: number): string {
        let label = '';
        const hours = Math.ceil(value * 24);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            label += days + (days === 1 ? ' day' : ' days');
        }
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
            if (label) label += ' ';
            label += remainingHours + (remainingHours === 1 ? ' hour' : ' hours');
        }
        return label;
    }

    toggleDataset(index: number) {
        if (!this.chart?.chart) return;
        const chartInstance = this.chart.chart;
        const isHidden = chartInstance.getDatasetMeta(index).hidden;

        if (isHidden) {
            chartInstance.show(index);
        } else {
            chartInstance.hide(index);
        }
    }

    isDatasetVisible(index: number): boolean {
        if (!this.chart?.chart) return true;
        return !this.chart.chart.getDatasetMeta(index).hidden;
    }

    hasHiddenDatasets(): boolean {
        if (!this.chart?.chart) return false;
        return this.chart.chart.data.datasets.some((_: any, index: number) => !this.isDatasetVisible(index));
    }

    getHiddenQueueIds(): string[] {
        if (!this.chart?.chart) return [];
        return this.chart.chart.data.datasets
            .filter((_: any, index: number) => !this.isDatasetVisible(index))
            .map((d: any) => d.queueId);
    }

    applyHiddenQueueIds(ids: string[]): void {
        if (!this.chart?.chart) return;
        this.chart.chart.data.datasets.forEach((d: any, index: number) => {
            if (ids.includes(d.queueId)) {
                this.chart.chart.hide(index);
            } else {
                this.chart.chart.show(index);
            }
        });
        this.chart.chart.update('none');
    }

    resetAllDatasets(): void {
        if (!this.chart?.chart) return;
        this.chart.chart.data.datasets.forEach((_: any, index: number) => {
            this.chart.chart.show(index);
        });
        this.chart.chart.update();
    }
}
